/**
 * RequireProId - Higher-Order Component that validates proId is set for Upercut users
 * Redirects to dashboard if proId is not set
 * Pro users bypass this check as they don't need proId
 */

import { useAuth } from '@contexts/AuthContext';
import { useProId } from '@contexts/ProIdContext';
import { Paper } from '@mui/material';
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import ActiveProIndicator from './ActiveProIndicator';
import { useDrawer } from './Sidebar/DrawerContext';

interface RequireProIdProps {
  children: React.ReactNode;
  redirectTo?: string;
  showSelector?: boolean; // Show selector instead of redirecting
}

/**
 * HOC that requires proId to be set for Upercut users
 * 
 * @param {ReactNode} children - The component to render if proId is set
 * @param {string} redirectTo - Path to redirect if proId is not set (default: '/dashboard')
 * @param {boolean} showSelector - If true, shows selector instead of redirecting
 * 
 * @example
 * // Redirect if proId not set
 * <RequireProId>
 *   <ManageBrandsPage />
 * </RequireProId>
 * 
 * @example
 * // Show selector if proId not set
 * <RequireProId showSelector>
 *   <ManageBrandsPage />
 * </RequireProId>
 */
const RequireProId: React.FC<RequireProIdProps> = ({
  children,
  redirectTo = '/dashboard',
  showSelector = false,
}) => {
  const { isUpercut, isPro, isAuthenticated } = useAuth();
  const { isProIdRequired, isProIdSet } = useProId();
      const { open, drawerWidth } = useDrawer();
  const location = useLocation();

  // Not authenticated - let RequireAuth handle this
  if (!isAuthenticated) {
    return <>{children}</>;
  }

  // Pro users don't need proId selection - they use their own ID
  if (isPro) {
    return <>{children}</>;
  }

  // Upercut users must have proId set
  if (isUpercut && isProIdRequired && !isProIdSet) {
    if (showSelector) {
      return (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
            paddingLeft: open ? `${drawerWidth + 16}px` : '16px', // Account for drawer width + spacing
          }}
        >
          <ActiveProIndicator variant="inline" size="small" />
        </Paper>
      );
    }

    // Redirect to dashboard with return URL
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // All checks passed - render children
  return <>
    <Paper
      elevation={0}
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
        paddingLeft: open ? `${drawerWidth + 16}px` : '16px', // Account for drawer width + spacing
      }}
    >
      <ActiveProIndicator variant="inline" size="small" />
    </Paper>
    {children}
  </>;
};

/**
 * Hook version of RequireProId for use in functional components
 * Returns boolean indicating if proId requirement is met
 * 
 * @example
 * const MyComponent = () => {
 *   const canProceed = useRequireProId();
 *   
 *   if (!canProceed) {
 *     return <ProIdSelector />;
 *   }
 *   
 *   return <div>Content</div>;
 * };
 */
export const useRequireProId = (): boolean => {
  const { isUpercut, isPro, isAuthenticated } = useAuth();
  const { isProIdSet } = useProId();

  if (!isAuthenticated) {
    return false;
  }

  // Pro users always pass
  if (isPro) {
    return true;
  }

  // Upercut users need proId set
  if (isUpercut) {
    return isProIdSet;
  }

  // Other user types
  return false;
};

export default RequireProId;
